/** /api-requests (+ /api-requests/:id/:tab? for the detail dialog overlay)
 *
 * Ops-wide inbound webhook / API call log across tenants, driven by the
 * reusable QuerySearch query-builder widget (same contract as /import-runs/,
 * over API_Request — integer row ids). The serialized query lives in `?q=`; a
 * tenant picker (`?tenant=12,845`, one or several operators) locks the query's
 * tenant and injects `tour_operator_id`. Server-side pagination + sorting; all
 * column filtering is delegated to the widget.
 *
 * demo_api_request is very large, fast-growing and unpartitioned — every
 * search namespace is an exact, index-backed match, and the grid SEEDS a
 * 7-day `created` window on first visit (no `?q=`, no `?preset=`).
 *
 * Batch ops (manage perm): Reimport — replay each request's stored webhook
 * body through its integration; Flush queues — kick processing of any
 * non-empty Redis booking queue the selected requests touch.
 */
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import * as apiRequestsApi from '@/api/apiRequests';
import type { ApiRequestListItem } from '@/api/apiRequests';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { BatchSelection } from '@/api/batch';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { type BatchActionDef, type BatchRefusals } from '@/components/DataGrid/batchTypes';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { countLabelDisplayedRows } from '@/components/DataGrid/CountTotalLabel';
import { useCountMode } from '@/components/DataGrid/useCountMode';
import { useGridCountMode } from '@/hooks/useUserPreferences';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  equalityOperator,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { createdWindowNode, hasCreatedTimeBound } from '@/features/delivery-logs/createdWindow';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { formatTenantIds } from '@/scope/tenantIds';
import { usePageTenant } from '@/scope/usePageTenant';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { withoutThreadParam } from '@/features/ai-diag/useThreadParam';

import { FlushQueuesBatchDialog } from './FlushQueuesBatchDialog';
import { ReimportApiRequestsDialog } from './ReimportApiRequestsDialog';
import { ApiRequestDetailDialog } from './detail/ApiRequestDetailDialog';
import { buildColumns } from './apiRequestsColumns';
import { buildApiRequestNamespaces } from './apiRequestNamespaces';

const DEFAULT_PAGE_SIZE = 50;

const NO_TENANTS: TenantPickerValue[] = [];

const DEFAULT_HIDDEN = ['booking_status', 'request_id', 'uuid', 'ip', 'url', 'error', 'view'];

/** Presets store the filter state as the raw `?q=` DSL text — it IS the
 *  canonical form (round-trips through parse/serialize), same as delivery-logs. */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q: q } : ({} as Record<string, string>);
  },
};

/** Drop every `tenant` condition from a tree (the picker owns that filter). */
function stripTenantConds(nodes: FilterNode[]): { nodes: FilterNode[]; removed: boolean } {
  let removed = false;
  const walk = (list: FilterNode[]): FilterNode[] => {
    const out: FilterNode[] = [];
    for (const n of list) {
      if (n.kind === 'cond' && n.namespace === 'tenant') {
        removed = true;
        continue;
      }
      if (n.kind === 'group') {
        const children = walk(n.children);
        if (children.length === 0) continue;
        if (children.length === 1) out.push(children[0]!);
        else out.push({ ...n, children });
        continue;
      }
      out.push(n);
    }
    return out;
  };
  return { nodes: walk(nodes), removed };
}

export function ApiRequestsPage() {
  const snackbar = useSnackbar();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: dialogId, tab: dialogTab } = useParams<{ id?: string; tab?: string }>();
  const apiRef = useGridApiRef();
  const namespaces = useMemo(() => buildApiRequestNamespaces(), []);

  // Effective tenants: URL ?tenant= > console scope (src/scope/usePageTenant.ts),
  // a list (`?tenant=12,845`) — the page takes the whole scope.
  const pageTenant = usePageTenant({ multiple: true });
  // Drives the preset defaults' tenant COLUMN only (shown unless exactly one
  // tenant is named); the filtering value is resolved below, once ?q= has been
  // parsed and scope suppression is known.
  const defaultShowTenant = pageTenant.ids.length !== 1;

  const presets = useGridPresets({
    grid: 'api_requests',
    defaultOrder: useMemo(
      () =>
        buildColumns({
          showTenant: defaultShowTenant,
          onQuickFilter: () => undefined,
          detailPath: (id) => `/api-requests/${id}/info`,
        }).map((c) => c.field),
      [defaultShowTenant],
    ),
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  const q = presets.getParam('q') ?? '';
  const ordering = presets.ordering;

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // A `tenant=` condition in ?q= — typed, or carried by a loaded preset —
  // already names a tenant. The ambient console scope then stands down rather
  // than AND-ing a second, conflicting tenant onto the query. An explicitly
  // PICKED tenant still wins and strips those conditions (effect below).
  const queryNamesTenant = useMemo(() => stripTenantConds(tree).removed, [tree]);
  const tenants = pageTenant.fromScope && queryNamesTenant ? NO_TENANTS : pageTenant.values;
  /** What the API's tour_operator_id param carries: `'12,845'` (D1). */
  const tenantApiValue = formatTenantIds(tenants.map((t) => t.id)) ?? undefined;

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, pageTenant.raw]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  // Seed the default bounded `created` window on first visit — only when
  // neither an explicit query nor a preset drives the page. Unlike the
  // import-runs grid this is not just a nicety: unbounded browsing of this
  // table leans entirely on the created_ts index, and every extra chip the
  // operator adds is exact/indexed too.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (sp.get('q') !== null || sp.get('preset') !== null) return;
    setQuery([createdWindowNode()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a tenant is picked and the tree still carries tenant conditions,
  // strip them and rewrite ?q= (the picker is authoritative for tenant).
  useEffect(() => {
    // Never for a scope-derived tenant: the console scope must not rewrite the
    // user's (or a preset's) query — that would also diverge a loaded preset.
    if (!tenantApiValue || pageTenant.fromScope) return;
    const { nodes, removed } = stripTenantConds(tree);
    if (removed) {
      const next = new URLSearchParams(sp);
      presets.beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
      snackbar.show('Tenant filter replaced by the picker above.', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantApiValue, tree]);

  const onTenantChange = pageTenant.setTenants;

  // Quick filters from cell popovers: append an equality chip for the
  // namespace (tenant goes through the picker instead — its chip would be
  // stripped right back out).
  const onQuickFilter = useCallback(
    (namespace: string, value: string) => {
      if (namespace === 'tenant') {
        pageTenant.setRaw(value);
        return;
      }
      const cfg = namespaces[namespace];
      const operator = cfg ? equalityOperator(cfg.operators) : null;
      if (!operator) return;
      setQuery([...tree, { kind: 'cond', namespace, operator, value }]);
    },
    [namespaces, tree, setQuery, pageTenant],
  );

  // Detail-dialog routes keep the grid's query string so closing the dialog
  // restores the exact search.
  const detailPath = useCallback(
    (id: number) => `/api-requests/${id}/info${withoutThreadParam(location.search)}`,
    [location.search],
  );

  const lockedFilters = tenantApiValue
    ? { tenant: { operator: 'iexact', value: tenantApiValue } }
    : undefined;
  // The suggestion endpoints here are the bookings autocompletes, which take the list.
  const extraFilterValues = tenantApiValue ? { tour_operator_id: tenantApiValue } : undefined;

  // The backend REQUIRES a created time bound on every search — mirror the
  // rule here so a windowless query never fires (deleted chip, hand-edited
  // ?q=, windowless preset): the grid shows a prompt instead of a 400.
  const hasWindow = useMemo(() => hasCreatedTimeBound(tree), [tree]);

  // Row-total mode, part 1: the persisted override. Read before the query
  // because it belongs in the query key — switching modes must re-issue the
  // request. Part 2 (useCountMode) needs the response and comes after.
  const countPref = useGridCountMode('api_requests');

  // The filter identity of the current query, shared by the list call and the
  // count-only call so the total can never describe a different set than the
  // rows on screen.
  const countOnlyParams = useMemo(
    () => ({
      ordering,
      ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
      filters: toWireAst(tree),
    }),
    [ordering, tenantApiValue, tree],
  );

  // Toolbar Refresh + auto-refresh. Polling pauses on the extended-timeout
  // tier (each such request is audited at the maximum statement budget).
  const autoRefresh = useGridAutoRefresh({ grid: 'api_requests' });
  const query = useQuery({
    queryKey: [
      'api-requests',
      pagination.page,
      pagination.pageSize,
      ordering,
      tenantApiValue ?? null,
      q,
      extendedTimeout,
      countPref.mode,
    ],
    queryFn: () =>
      apiRequestsApi.list({
        page: pagination.page + 1,
        page_size: pagination.pageSize,
        ordering,
        ...(tenantApiValue ? { tour_operator_id: tenantApiValue } : {}),
        filters: toWireAst(tree),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
        // Only when the user overrode the mode; omitting it keeps the backend's
        // own (deferred) default, which is what makes a cold load free.
        ...(countPref.mode ? { count_mode: countPref.mode } : {}),
      }),
    placeholderData: keepPreviousData,
    ...autoRefresh.queryOptions(extendedTimeout),
    enabled: !presets.blocking && hasWindow,
  });

  // -------------------------------------------------------------------------
  // Batch: Reimport + Flush queues — raw DataGridPro, so the batch layer is
  // wired by hand (the delivery-logs convention) with integer row ids.
  // -------------------------------------------------------------------------
  const rows = useMemo(() => query.data?.results ?? [], [query.data?.results]);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  const queryIdentity = useMemo(
    () => JSON.stringify({ q, tenant: tenantApiValue, ordering, pageSize: pagination.pageSize }),
    [q, tenantApiValue, ordering, pagination.pageSize],
  );

  // Row totals. This grid defers its count server-side, so `count` arrives as
  // -1 and the pager renders "Show total" instead of a number; MUI paginates on
  // `paginationMeta.hasNextPage`. See components/DataGrid/useCountMode.ts.
  const count = useCountMode({
    pref: countPref,
    queryIdentity,
    response: query.data,
    countOnly: () => apiRequestsApi.count(countOnlyParams),
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
    ...(query.data ? { rowsOnPage: query.data.results.length } : {}),
    onResetPage: () => setPagination((p) => ({ ...p, page: 0 })),
  });
  const rowCount = count.rowCount;
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  const selectedIds = useMemo(() => selectionModel.map((i) => Number(i)), [selectionModel]);
  const selectedCount = allMatching ? (count.total ?? selectedIds.length) : selectedIds.length;

  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);

  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (ordering) usp.set('ordering', ordering);
    if (tenantApiValue) usp.set('tour_operator_id', tenantApiValue);
    const wire = toWireAst(tree);
    if (wire.length > 0) usp.set('filters', JSON.stringify(wire));
    return usp.toString();
  }, [ordering, tenantApiValue, tree]);

  const batchAction = useBatchAction({
    resource: 'api_requests',
    queryKey: ['api-requests'],
    onCleared: clearSelection,
  });

  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? filterQuery }),
    [batchAction, filterQuery],
  );

  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'reimport',
        label: 'Reimport…',
        action: 'reimport',
        permission: 'console_api_requests_manage',
        dialog: (ctx) => <ReimportApiRequestsDialog ctx={ctx} />,
      },
      {
        key: 'flush_queues',
        label: 'Flush queues…',
        action: 'flush_queues',
        permission: 'console_api_requests_manage',
        dialog: (ctx) => <FlushQueuesBatchDialog ctx={ctx} />,
      },
    ],
    [],
  );

  const batchEnabled = batchActions.length > 0;

  const columns = useMemo(
    () =>
      orderColumns(
        buildColumns({ showTenant: tenants.length !== 1, onQuickFilter, detailPath }),
        presets.orderedFields,
      ),
    [tenants.length, onQuickFilter, detailPath, presets.orderedFields],
  );

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  const hasSearch = q.trim().length > 0 || tenants.length > 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="api-requests"
        title="API Requests"
        subtitle="Inbound webhooks and API calls across every tenant — search is exact-match over indexed fields only."
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'flex-start' }}>
        <Box sx={{ width: 280, flexShrink: 0 }}>
          <TenantPicker multiple value={tenants} onChange={onTenantChange} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            {...(lockedFilters ? { lockedFilters } : {})}
            {...(extraFilterValues ? { extraFilterValues } : {})}
            placeholder="Search API requests…  e.g. booking=BOKU-123456 & errors=yes"
          />
        </Box>
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}

      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}

      {!hasWindow && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setQuery([...tree, createdWindowNode()])}
            >
              Add last 7 days
            </Button>
          }
        >
          Every search needs a created time window — the API request log is too large to scan
          unbounded.
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<ApiRequestListItem>
          apiRef={apiRef}
          rows={rows}
          rowCount={rowCount}
          {...(count.paginationMeta ? { paginationMeta: count.paginationMeta } : {})}
          getRowId={(r) => r.id}
          columns={columns}
          loading={query.isLoading || query.isFetching}
          sx={{
            flex: 1,
            minHeight: 360,
            // Auto row heights (the multiline Booking column) — keep single-line
            // cells vertically centered with a stable minimum row height.
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', minHeight: 44 },
          }}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 52}
          pagination
          paginationMode="server"
          sortingMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          pageSizeOptions={[25, 50, 100, 200]}
          checkboxSelection={batchEnabled}
          disableRowSelectionOnClick
          disableColumnFilter
          {...(batchEnabled
            ? {
                rowSelectionModel: selectionModel,
                onRowSelectionModelChange: handleSelectionChange,
                keepNonExistentRowsSelected: true,
              }
            : {})}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasSearch ? 'No API requests match this search.' : 'Search to find API requests.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            pagination: {
              labelDisplayedRows: countLabelDisplayedRows({
                state: count.state,
                total: count.total,
                canRequest: true,
                onRequest: count.requestTotal,
              }),
            },
            toolbar: {
              // No time window → the list query is disabled (and refetch() ignores that).
              refresh: gridRefreshToolbar(query, autoRefresh, {
                paused: extendedTimeout,
                disabled: !hasWindow,
              }),
              hideFilterButton: true,
              exportFileName: 'api-requests',
              presets: presets.toolbar,
              countMode: { mode: count.mode, setMode: count.setMode },
              ...(batchEnabled
                ? {
                    batch: {
                      actions: batchActions,
                      ids: selectedIds,
                      allMatching,
                      count: selectedCount,
                      matched: count.total,
                      filterQuery,
                      runStandard,
                      clearSelection,
                      onSelectAllMatching: () => setAllMatching(true),
                      isPending: batchAction.isPending,
                    },
                  }
                : {}),
            } as GridToolbarProps,
          }}
        />
      )}

      {query.data?.count_is_capped && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          10,000+ results — refine your search to see more precise counts.
        </Typography>
      )}

      {dialogId && (
        <ApiRequestDetailDialog
          requestId={dialogId}
          tab={dialogTab}
          // The Diagnose tab keeps the open chat in `?thread`; the grid must
          // not inherit it on close (this page carries its search string
          // through every navigation).
          onClose={() => navigate(`/api-requests${withoutThreadParam(location.search)}`)}
          onNavigateTab={(t) => navigate(`/api-requests/${dialogId}/${t}${location.search}`)}
        />
      )}
    </Box>
  );
}
